import React, { useCallback, useMemo } from "react";
import OutsideClickHandler, {
  Props as OutsideClickHandlerProps,
} from "react-outside-click-handler";
import { Manager, Popper, Reference } from "react-popper";
import debounce from "lodash.debounce";
import partition from "lodash.partition";
import { Modifiers, Placement } from "popper.js";

import { EmotionComponent, styled } from "@catchoftheday/theme";
import { useControlled } from "@catchoftheday/utilities";

import { Box, BoxProps } from "../Box";

const ARROW_X_SIZE = "7px";
const ARROW_Y_SIZE = "10px";

function arrowPosition(placement = ""): PopoverPlacement {
  const positions: PopoverPlacement[] = ["top", "bottom", "left", "right"];
  return positions.find((val) => val && placement.includes(val));
}

function autoSizing(data) {
  const leftReferenceOffset = Math.ceil(data.offsets.reference.left);
  data.offsets.popper.left = leftReferenceOffset;
  data.offsets.popper.right = data.offsets.reference.right;
  data.offsets.popper.width = data.styles.width = data.offsets.reference.width;
  if (data.placement === "bottom") {
    data.styles.transform = `translate3d(${leftReferenceOffset}px, ${data.offsets.reference.bottom}px, 0)`;
  }
  return data;
}

export type ContentWrapperProps = {
  placement: string;
  hideArrow?: boolean;
  color?: string;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

const Arrow: EmotionComponent<ContentWrapperProps> = styled(
  "div"
)<ContentWrapperProps>`
  height: 0;
  width: 0;
  border: solid transparent;
  border-width: ${ARROW_Y_SIZE} ${ARROW_X_SIZE};
  position: absolute;

  ${(props) => {
    const placement = arrowPosition(props.placement);
    return (
      placement &&
      `
      border-${placement}-color: ${props.color || "black"};
      ${placement}: 100%;
    `
    );
  }};
`;

export type ControlWrapperProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

const ContentWrapper: EmotionComponent<ContentWrapperProps & BoxProps> = styled(
  Box
)<ContentWrapperProps & BoxProps>`
  ${(props) => {
    const placement = arrowPosition(props.placement);
    let marginPos = "";

    switch (placement) {
      case "top": {
        marginPos = "bottom";
        break;
      }
      case "bottom": {
        marginPos = "top";
        break;
      }
      case "left": {
        marginPos = "right";
        break;
      }
      case "right": {
        marginPos = "left";
        break;
      }
      default:
        break;
    }

    return (
      marginPos &&
      !props.hideArrow &&
      `
      margin-${marginPos}: ${ARROW_Y_SIZE};
    `
    );
  }}
`;
export type PopoverControlChildrenProps = {
  /** Sets isOpen to true and opens the Popover. */
  openPopover: () => void;
  /** Sets isOpen to false and closes the Popover. */
  closePopover: () => void;
  /** Flag which controls the display of Popover content. */
  isOpen: boolean;
  /** Closes all Popover content and resets internal state. Render prop for PopoverControl only. */
  handleOnControllerLeave: () => void;
  /** Handles keyDown events. Render prop for PopoverControl. Not compatible with handleOnFocus. */
  handleOnKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  /** Handles focus events. Render prop for PopoverControl and also applies to all ContentWrapper components.
   * Not compatible with handleOnKeyDown.
   */
  handleOnFocus: () => void;
  /** Handles blur events. Render prop for PopoverControl and also applies to all ContentWrapper components.  */
  handleOnBlur: () => void;
  /** Handles mouseenter events. Render prop for PopoverControl and also applies to all ContentWrapper components.  */
  handleOnMouseEnter: () => void;
  /** Handles mouseleave events. Render prop for PopoverControl and also applies to all ContentWrapper components.  */
  handleOnMouseLeave: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: React.Ref<any>;
};

export type PopoverControlProps = {
  /** Children render props  */
  children?: (props: PopoverControlChildrenProps) => React.ReactNode;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export type PopoverContentChildrenProps = {
  openPopover: () => void;
  closePopover: () => void;
  isOpen: boolean;
};

export type PopoverContentRenderProps = (
  props: PopoverContentChildrenProps
) => React.ReactNode;

export type PopoverContentProps = {
  /** Children render props  */
  children?: React.ReactNode | PopoverContentRenderProps;
};

export type PopoverPlacement = Placement | undefined;

export type PopoverProps = {
  children?: React.ReactNode;
  /** Preferred position of Popover  */
  placement?: PopoverPlacement;
  /** Should Popover content container match full width of Popover control, only works on bottom or top placement */
  fullWidth?: boolean;
  /** Should hide Popover when click outside  */
  hideOnClickOutside?: boolean;
  /** Should hide Popover when mouse leaves element or is blurred. Won't work with hideOnClickOutside */
  hideOnLeave?: boolean;
  /** Time in milliseconds before Popover hides via onMouseLeave or onBlur. Unused if hideOnLeave == false */
  hideOnLeaveInterval?: number;
  /** Powers keyboard navigation (arrow keys, space, enter, etc.). Used with the handleOnKeyDown render prop. */
  enhancedKeyboardNavigation?: boolean;
  /** Should hide arrow */
  hideArrow?: boolean;
  /** Arrow Color */
  arrowColor?: string;
  /** Outside click handler props, see https://github.com/airbnb/react-outside-click-handler */
  outsideClickHandlerProps?: Partial<OutsideClickHandlerProps>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  innerRef?: React.Ref<any>;
  /** Useful when you want to forcefully control isOpen status of Popover */
  isOpen?: boolean;
  /** modifier for popper*/
  modifier?: Modifiers;
} & BoxProps;

export const Popover: React.FC<PopoverProps> = ({
  children,
  placement: initialPlacement = "bottom",
  fullWidth = false,
  hideOnClickOutside = false,
  hideOnLeave = false,
  hideOnLeaveInterval = 200,
  enhancedKeyboardNavigation = false,
  hideArrow = false,
  arrowColor = "black",
  zIndex = 1,
  outsideClickHandlerProps,
  innerRef,
  isOpen: isOpenProp,
  modifier,
  ...boxProps
}) => {
  const [isOpen, setIsOpen] = useControlled<boolean>(false, isOpenProp);
  const [isFocus, setIsFocus] = useControlled<boolean>(false);
  const [isMouseEntered, setIsMouseEntered] = useControlled<boolean>(false);
  const safeToCloseContent = !(isMouseEntered && isFocus);

  function openPopover() {
    setIsOpen(true);
  }

  function closePopover() {
    setIsOpen(false);
  }

  const childrenArray = React.Children.toArray(children);
  const [control, content] = partition(
    childrenArray,
    (child) => child?.type?.displayName === "PopoverControl"
  );

  const controlRenderProps = control?.[0]?.props as PopoverControlProps;
  const contentRenderProps = content?.[0]?.props as PopoverContentProps;

  const closePopoverTimeout = debounce(() => {
    setIsOpen(false);
    setIsFocus(false);
    setIsMouseEntered(false);
  }, hideOnLeaveInterval);

  const closePopoverCallback = useCallback(closePopoverTimeout, [
    closePopoverTimeout,
  ]);

  const handleOnControllerLeave = useCallback(() => {
    if (hideOnLeave) {
      closePopoverCallback();
    }
  }, [hideOnLeave, closePopoverCallback]);

  const keysToOpenPopover = useMemo(() => {
    return [
      "Enter",
      "Space",
      initialPlacement.includes("bottom")
        ? "ArrowDown"
        : initialPlacement.includes("right")
        ? "ArrowRight"
        : null,
    ];
  }, [initialPlacement]);

  const handleOnKeyDown = useCallback(
    ({ code }: React.KeyboardEvent<HTMLElement>) => {
      if (enhancedKeyboardNavigation) {
        if (!isOpen && keysToOpenPopover.includes(code)) {
          setIsOpen(true);
        }
      }
    },
    [isOpen, enhancedKeyboardNavigation, keysToOpenPopover, setIsOpen]
  );

  const handleOnFocus = useCallback(() => {
    setIsOpen(true);
    setIsFocus(true);
    setIsMouseEntered(false);
    closePopoverTimeout.cancel();
  }, [setIsOpen, setIsFocus, setIsMouseEntered, closePopoverTimeout]);

  const handleOnBlur = useCallback(() => {
    if (hideOnLeave && safeToCloseContent) {
      closePopoverCallback();
    } else {
      setIsFocus(false);
    }
  }, [hideOnLeave, safeToCloseContent, setIsFocus, closePopoverCallback]);

  const handleOnMouseEnter = useCallback(() => {
    setIsOpen(true);
    setIsMouseEntered(true);
    setIsFocus(false);
    closePopoverTimeout.cancel();
  }, [setIsOpen, setIsMouseEntered, setIsFocus, closePopoverTimeout]);

  const handleOnMouseLeave = useCallback(() => {
    if (hideOnLeave && safeToCloseContent) {
      closePopoverCallback();
    } else {
      setIsMouseEntered(false);
    }
  }, [
    hideOnLeave,
    safeToCloseContent,
    closePopoverCallback,
    setIsMouseEntered,
  ]);

  if (!controlRenderProps) {
    throw new Error("PopoverControl is required");
  }

  return (
    <Box aria-expanded={isOpen} {...boxProps}>
      <OutsideClickHandler
        display="inline"
        onOutsideClick={hideOnClickOutside ? closePopover : () => null}
        {...outsideClickHandlerProps}
      >
        <Manager>
          <Reference innerRef={innerRef}>
            {({ ref }) => (
              <>
                {controlRenderProps.children &&
                  controlRenderProps.children({
                    ref,
                    openPopover,
                    closePopover,
                    isOpen,
                    handleOnControllerLeave,
                    handleOnKeyDown,
                    handleOnFocus,
                    handleOnBlur,
                    handleOnMouseEnter,
                    handleOnMouseLeave,
                  })}
              </>
            )}
          </Reference>
          {isOpen && (
            <Popper
              placement={initialPlacement}
              modifiers={{
                autoSizing: {
                  enabled:
                    fullWidth && ["top", "bottom"].includes(initialPlacement),
                  fn: autoSizing,
                  order: 840,
                },
                ...modifier,
              }}
            >
              {({ ref, style, placement, arrowProps }) => (
                <ContentWrapper
                  onFocus={handleOnFocus}
                  onBlur={handleOnBlur}
                  onKeyDown={handleOnKeyDown}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                  ref={ref}
                  style={{
                    ...style,
                  }}
                  zIndex={zIndex}
                  placement={placement || initialPlacement}
                  hideArrow={hideArrow}
                >
                  {isOpen &&
                  contentRenderProps &&
                  typeof contentRenderProps.children === "function"
                    ? contentRenderProps.children({
                        openPopover,
                        closePopover,
                        isOpen,
                      })
                    : content}
                  {!hideArrow && (
                    <Arrow
                      ref={arrowProps.ref}
                      style={arrowProps.style}
                      placement={placement || initialPlacement}
                      color={arrowColor}
                    />
                  )}
                </ContentWrapper>
              )}
            </Popper>
          )}
        </Manager>
      </OutsideClickHandler>
    </Box>
  );
};

Popover.displayName = "Popover";

export const PopoverControl: React.FC<PopoverControlProps> = ({ children }) => (
  <>{children}</>
);
PopoverControl.displayName = "PopoverControl";

export const PopoverContent: React.FC<PopoverContentProps> = ({ children }) => (
  <>{children}</>
);

export default Popover;
